import {
  Grid,
  Box,
  TextField,
  Button,
  Switch,
  Typography,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import Axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { Loading } from "../../components/shared/loading";
import { useAdminStyles } from "../../components/admin/adminStyles";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { useToast } from "../../components/shared/Toast";

import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertPhotoOutlinedIcon from "@material-ui/icons/InsertPhotoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import SaveIcon from "@material-ui/icons/Save";

const headKey = "qweqeasdj123n1jasndjn2j3njsdn12i891bdjsdu1283djsd1231231";

export const UpdateBanner = () => {
  const classes = useAdminStyles();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [subtitle, setSubtitle] = useState("");
  const [banner, setBanner] = useState({ raw: "", preview: "" });
  const [status, setStatus] = useState(true);
  const [oldImage, setOldImage] = useState("");
  const [err, setErr] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const formData = new FormData();
    formData.append("idd", id);
    Axios.post("/api/select_single_banner", formData, {
      headers: { headKey: headKey },
    }).then((res) => {
      if (res.data.status) {
        const d = res.data.data;
        setSubtitle(d.sub_title || "");
        if (d.banner_url) setOldImage(d.banner_url);
        setStatus(d.status === 1);
        setIsLoading(false);
      } else {
        toast.error("Could not load banner");
        navigate("/dash/banners");
      }
    });
  }, [id, navigate, toast]);

  const handleImageChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Please select only JPEG, JPG, PNG or WebP images.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be smaller than 8 MB.");
      return;
    }
    setBanner({
      preview: URL.createObjectURL(file),
      raw: file,
    });
  };

  const onSubmit = () => {
    setErr(0);
    if (!subtitle.trim()) {
      setErr(1);
      toast.error("Please enter a banner subtitle.");
      return;
    }
    if (!banner.raw && !oldImage) {
      setErr(2);
      toast.error("Please select a banner image.");
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    if (banner.raw) formData.append("bannerurl", banner.raw);
    formData.append("idd", id);
    formData.append("subtitle", subtitle.trim());
    formData.append("oldimage", oldImage);
    formData.append("status", status ? "1" : "0");

    Axios.post("/api/update_banner", formData, {
      headers: { headKey: headKey },
    })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status) {
          toast.success(res.data.msg || "Banner updated");
          navigate("/dash/banners");
        } else {
          toast.error(res.data.msg || "Something went wrong. Try again.");
        }
      })
      .catch(() => {
        setSubmitting(false);
        toast.error("Network error. Please try again.");
      });
  };

  if (isLoading) return <Loading />;

  return (
    <div>
      <Helmet>
        <title>Edit Banner - Admin | Arawinz Soft Solutions</title>
      </Helmet>

      <AdminPageHeader
        eyebrow="Banners"
        title="Edit banner"
        subtitle="Update the banner subtitle, image, and visibility."
        backTo="/dash/banners"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <TextField
            label="Subtitle"
            variant="outlined"
            error={err === 1}
            value={subtitle}
            size="small"
            onChange={(e) => setSubtitle(e.target.value)}
            required
            fullWidth
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box className={classes.statusRow}>
            <Switch
              checked={status}
              onChange={() => setStatus(!status)}
              color="primary"
            />
            <span>
              Status:{" "}
              <span className={status ? "on" : "off"}>
                {status ? "Visible" : "Hidden"}
              </span>
            </span>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.sectionTitle}>Banner image</Typography>
          <Box className={classes.uploadWrap}>
            <input
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
              id="banner-image-upload"
              type="file"
            />
            <label htmlFor="banner-image-upload" style={{ display: "block" }}>
              <Box
                className={`${classes.uploadBox} ${
                  banner.raw ? classes.uploadBoxFilled : ""
                }`}
              >
                <Box className={classes.uploadIconWrap}>
                  {banner.raw ? (
                    <InsertPhotoOutlinedIcon />
                  ) : (
                    <CloudUploadOutlinedIcon />
                  )}
                </Box>
                <Box className={classes.uploadInfo}>
                  {banner.raw ? (
                    <>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <CheckCircleOutlineIcon
                          style={{ fontSize: 16, color: "#10b981" }}
                        />
                        <strong>{banner.raw.name}</strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        Click to choose a different file
                      </Typography>
                    </>
                  ) : (
                    <>
                      <span>
                        <strong>
                          {oldImage ? "Replace banner" : "Upload banner image"}
                        </strong>
                      </span>
                      <Typography className={classes.uploadHint}>
                        JPEG, PNG or WebP, up to 8 MB
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </label>
            <Box style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {oldImage && !banner.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    Current banner
                  </Typography>
                  <img
                    src={"/static/banners/" + oldImage}
                    alt="current"
                    className={classes.uploadPreview}
                    style={{ maxWidth: "100%", maxHeight: 240 }}
                  />
                </Box>
              )}
              {banner.preview && (
                <Box>
                  <Typography
                    className={classes.uploadHint}
                    style={{ marginBottom: 4 }}
                  >
                    New banner preview
                  </Typography>
                  <img
                    src={banner.preview}
                    alt="preview"
                    className={classes.uploadPreview}
                    style={{ maxWidth: "100%", maxHeight: 240 }}
                  />
                </Box>
              )}
            </Box>
            {err === 2 && (
              <Typography style={{ color: "#dc2626", fontSize: "0.78rem" }}>
                Please select an image.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box className={classes.formFooter}>
        <Button
          className={classes.secondaryBtn}
          onClick={() => navigate("/dash/banners")}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className={classes.primaryBtn}
          startIcon={!submitting && <SaveIcon />}
        >
          {submitting ? "Updating..." : "Save changes"}
        </Button>
      </Box>
    </div>
  );
};
